import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Colours from '../../design-system/colours';
import useAuthorized from '../../hooks/use-authorized';
import { Resource } from '../../common/model/type/resource.enum';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { mapShippingRateToTableDataFormat } from '../../common/utils/services/shipping-rate-utils';
import { useAdminShippingRateMutations } from '../../hooks/use-admin-shipping-rate-mutations';
import { getShippingRateById } from '../../services/admin/admin-shipping-rate.service';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvRecordDetailsPage from '../UI/atoms/OvRecordDetailsPage';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvRecordDetailsErrorState from '../UI/molecules/OvRecordDetailsErrorState';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvTable from '../UI/molecules/OvTable';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';

const LIST_PATH = '/shipping-rates';

const ShippingRateDetails = () => {
  const { t } = useTranslation();
  const { shippingRateId } = useParams() as { shippingRateId: string };
  const navigate = useNavigate();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);

  const {
    data: record,
    isFetching,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['admin-shipping-rate', shippingRateId],
    queryFn: () => getShippingRateById(shippingRateId),
    enabled: !!shippingRateId,
  });

  const { deleteRate, updateRate } = useAdminShippingRateMutations();

  const operations = useAuthorized(Resource.ShippingRate);
  const updateAllowed = operations.update || operations.supervise;

  const handleOnDelete = () => {
    setIsConfirmationDialogOpen(false);
    deleteRate.mutate(shippingRateId, {
      onSuccess: () => navigate(LIST_PATH),
    });
  };

  if (!record) {
    return (
      <OvRecordDetailsPage>
        {loadError ? (
          <OvRecordDetailsErrorState
            title={t('shippingRates.details.errorTitle')}
            error={loadError}
            onRetry={() => refetch()}
            backLabel={t('app.subPages.menu.shippingRates')}
            backPath={LIST_PATH}
          />
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </OvRecordDetailsPage>
    );
  }

  const error = loadError ?? updateRate.error ?? deleteRate.error;
  const isLoading = isFetching || updateRate.isPending || deleteRate.isPending;

  return (
    <OvRecordDetailsPage aria-busy={isLoading}>
      {error && <OvErrorMessage message={getAxiosErrorMessage(error)} />}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('shippingRates.details.breadcrumb')}
          backControl={
            <OvBackButton
              label={t('app.subPages.menu.shippingRates')}
              fallbackPath={LIST_PATH}
            />
          }
          current={record.internal_name}
          title={record.internal_name}
          titleAside={
            <OvStatusChip
              label={t(
                record.is_active
                  ? 'shippingRates.details.active'
                  : 'shippingRates.details.inactive'
              )}
              tone={record.is_active ? 'success' : 'neutral'}
            />
          }
          meta={
            <>
              <span>
                {record.id}
                <OvCopyToClipboardButton
                  text={record.id}
                  label={t('shippingRates.details.copyId')}
                />
              </span>
              {record.display_name && <span>{record.display_name}</span>}
              {record.created_at && (
                <span>
                  <OvDateParser
                    date={record.created_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            <OvRecordHeaderActions>
              <OvCompactButton
                tone="danger"
                icon={<DeleteIcon />}
                onClick={() => setIsConfirmationDialogOpen(true)}
              >
                {t('common.actions.delete')}
              </OvCompactButton>
            </OvRecordHeaderActions>
          }
        />

        <OvRecordDetailsSection title={t('shippingRates.details.section')}>
          <OvTable
            data={mapShippingRateToTableDataFormat(record)}
            theme="Light"
            onSaveRowValue={(request: Record<string, unknown>) =>
              updateRate.mutate({ id: shippingRateId, request })
            }
            editable={updateAllowed}
          />
        </OvRecordDetailsSection>
      </OvRecordDetailsCanvas>

      {isLoading && <OvLoadingIndicator position="fixed" />}

      <OvConfirmationDialog
        icon={<StyledDeleteIcon />}
        isOpen={isConfirmationDialogOpen}
        onCancel={() => setIsConfirmationDialogOpen(false)}
        onConfirm={handleOnDelete}
        description="shippingRates.dialogs.deleteShippingRateDescription"
        title="shippingRates.dialogs.deleteShippingRateTitle"
      />
    </OvRecordDetailsPage>
  );
};

export default ShippingRateDetails;

const StyledDeleteIcon = styled(DeleteSweepIcon)`
  && {
    box-sizing: content-box;
    padding: 0.5rem;
    background: ${Colours.OV_BASE};
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    text-align: center;
    display: block;
    margin: 1rem auto 0 auto;
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
